import { createContext, useReducer } from 'react';
import { supabase } from '../supabase/config';

export const PartyContext = createContext();

const partyReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PARTIES':
      return { ...state, parties: action.payload };
    case 'ADD_PARTY':
      return { ...state, parties: [action.payload, ...state.parties] };
    case 'UPDATE_PARTY':
      return { 
        ...state, 
        parties: state.parties.map(party => 
          party.id === action.payload.id ? action.payload : party
        ) 
      };
    case 'DELETE_PARTY':
      return { 
        ...state, 
        parties: state.parties.filter(party => party.id !== action.payload) 
      };
    default:
      return state;
  }
};

export const PartyContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(partyReducer, {
    parties: []
  });

  // Create a new party
  const createParty = async (partyData) => {
    try {
      // Get the current session instead of calling getUser
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData?.session?.user;
      
      if (!user) {
        throw new Error('You must be logged in to create a party');
      }
      
      const userId = user.id;
      
      // Generate a random join code (6 uppercase letters and numbers)
      const generateJoinCode = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 6; i++) {
          result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
      };
      
      // First, check the schema to see if host_id exists
      const { error: schemaError } = await supabase
        .from('parties')
        .select('host')
        .limit(1);
      
      if (schemaError) {
        console.error('Error checking schema:', schemaError);
        // If we can't check the schema, just use 'host' as it's the most likely field
        const newParty = {
          ...partyData,
          host: userId,
          attendees: [userId],
          rsvp_responses: {},
          items: [],
          messages: [],
          join_code: generateJoinCode(),
          created_at: new Date().toISOString()
        };
        
        const { data, error } = await supabase
          .from('parties')
          .insert([newParty])
          .select();
        
        if (error) throw error;
        
        const partyWithId = data[0];
        dispatch({ type: 'ADD_PARTY', payload: partyWithId });
        return partyWithId;
      }
      
      // If we got here, we know the schema has a 'host' field
      const newParty = {
        ...partyData,
        host: userId,
        attendees: [userId],
        rsvp_responses: {},
        items: [],
        messages: [],
        join_code: generateJoinCode(),
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('parties')
        .insert([newParty])
        .select();
      
      if (error) {
        console.error('Error inserting party:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        throw new Error('No data returned after creating party');
      }
      
      const partyWithId = data[0];
      dispatch({ type: 'ADD_PARTY', payload: partyWithId });
      return partyWithId;
    } catch (error) {
      console.error('Error in createParty:', error);
      throw error;
    }
  };

  // Update an existing party
  const updateParty = async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('parties')
        .update(updates)
        .eq('id', id)
        .select();
      
      if (error) throw error;
      
      const updatedParty = data[0];
      
      dispatch({ 
        type: 'UPDATE_PARTY', 
        payload: updatedParty
      });
      
      return updatedParty;
    } catch (error) {
      throw error;
    }
  };

  // Delete a party
  const deleteParty = async (id) => {
    try {
      const { error } = await supabase
        .from('parties')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      dispatch({ type: 'DELETE_PARTY', payload: id });
    } catch (error) {
      throw error;
    }
  };

  // Add user to party (invitation)
  const inviteToParty = async (partyId, userEmail) => {
    try {
      // First get the current party
      const { data: partyData, error: fetchError } = await supabase
        .from('parties')
        .select('invitees')
        .eq('id', partyId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Update with new invitee
      const invitees = partyData.invitees || [];
      if (!invitees.includes(userEmail)) {
        invitees.push(userEmail);
      }
      
      const { error } = await supabase
        .from('parties')
        .update({ invitees })
        .eq('id', partyId);
      
      if (error) throw error;
      
      // Send invitation email using Supabase Edge Function
      try {
        const { error: emailError } = await supabase.functions.invoke('send-invitation-email', {
          body: { partyId, inviteeEmail: userEmail }
        });
        
        if (emailError) {
          console.error('Error sending invitation email:', emailError);
        }
      } catch (emailError) {
        // Log the error but don't fail the invitation process
        console.error('Failed to send invitation email:', emailError);
      }
    } catch (error) {
      throw error;
    }
  };

  // RSVP to a party
  const rsvpToParty = async (partyId, userId, response) => {
    try {
      console.log('RSVP to party:', { partyId, userId, response });
      
      // Use the database function to handle RSVP
      const { data, error } = await supabase.rpc('handle_rsvp', {
        p_party_id: partyId,
        p_user_id: userId,
        p_response: response
      });
      
      if (error) {
        console.error('Error updating RSVP:', error);
        throw error;
      }
      
      console.log('RSVP updated successfully:', data);
      return data;
    } catch (error) {
      // If the database function doesn't exist yet, fall back to the old method
      if (error.message && error.message.includes('function handle_rsvp() does not exist')) {
        console.log('Falling back to manual RSVP update');
        return manualRsvpUpdate(partyId, userId, response);
      }
      
      console.error('RSVP error:', error);
      throw error;
    }
  };
  
  // Manual RSVP update (fallback method)
  const manualRsvpUpdate = async (partyId, userId, response) => {
    try {
      // First get the current party
      const { data: partyData, error: fetchError } = await supabase
        .from('parties')
        .select('rsvp_responses, attendees')
        .eq('id', partyId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching party for RSVP:', fetchError);
        throw fetchError;
      }
      
      console.log('Current party data:', partyData);
      
      // Update RSVP responses
      const rsvp_responses = partyData.rsvp_responses || {};
      rsvp_responses[userId] = response;
      
      // Update attendees list
      let attendees = partyData.attendees || [];
      
      if (response === 'attending') {
        // Add to attendees if not already there
        if (!attendees.includes(userId)) {
          attendees.push(userId);
        }
      } else {
        // Remove from attendees
        attendees = attendees.filter(id => id !== userId);
      }
      
      console.log('Updating party with:', { rsvp_responses, attendees });
      
      const { data, error } = await supabase
        .from('parties')
        .update({ 
          rsvp_responses,
          attendees
        })
        .eq('id', partyId)
        .select();
      
      if (error) {
        console.error('Error updating RSVP:', error);
        throw error;
      }
      
      console.log('RSVP updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Manual RSVP update error:', error);
      throw error;
    }
  };

  // Add an item to the "who brings what" list
  const addItem = async (partyId, item) => {
    try {
      // First get the current party
      const { data: partyData, error: fetchError } = await supabase
        .from('parties')
        .select('items')
        .eq('id', partyId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Update with new item
      const items = partyData.items || [];
      items.push(item);
      
      const { error } = await supabase
        .from('parties')
        .update({ items })
        .eq('id', partyId);
      
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  // Remove an item from the "who brings what" list
  const removeItem = async (partyId, item) => {
    try {
      // First get the current party
      const { data: partyData, error: fetchError } = await supabase
        .from('parties')
        .select('items')
        .eq('id', partyId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Remove the item
      const items = partyData.items.filter(i => 
        i.name !== item.name || i.broughtBy !== item.broughtBy
      );
      
      const { error } = await supabase
        .from('parties')
        .update({ items })
        .eq('id', partyId);
      
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  // Add a chat message
  const addMessage = async (partyId, message) => {
    try {
      console.log('Adding message to party:', partyId, message);
      
      // First get the current party
      const { data: partyData, error: fetchError } = await supabase
        .from('parties')
        .select('messages')
        .eq('id', partyId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching party messages:', fetchError);
        throw fetchError;
      }
      
      console.log('Current party messages:', partyData.messages);
      
      // Add new message
      const messages = partyData.messages || [];
      messages.push({
        ...message,
        createdAt: new Date().toISOString()
      });
      
      // Update the party with the new messages
      const { data, error } = await supabase
        .from('parties')
        .update({ messages })
        .eq('id', partyId)
        .select();
      
      if (error) {
        console.error('Error updating party messages:', error);
        throw error;
      }
      
      console.log('Messages updated successfully:', data);
      
      // Broadcast a custom event to notify all clients about the new message
      // This is a fallback in case the real-time subscription doesn't work
      try {
        await supabase.rpc('broadcast_party_update', {
          party_id: partyId,
          update_type: 'new_message'
        });
      } catch (broadcastError) {
        // This is optional, so we just log the error but don't throw
        console.log('Broadcast error (non-critical):', broadcastError);
      }
      
      return data;
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  };

  // Join a party by code
  const joinPartyByCode = async (code) => {
    try {
      // Get the current user
      const { data: userData } = await supabase.auth.getUser();
      if (!userData || !userData.user) {
        throw new Error('You must be logged in to join a party');
      }
      
      // Call the database function to join the party
      const { data, error } = await supabase.rpc('join_party_by_code', {
        p_code: code.toUpperCase(),
        p_user_id: userData.user.id
      });

      if (error) throw error;
      
      // The response is the party ID
      return data;
    } catch (error) {
      console.error('Error joining party by code:', error);
      throw error;
    }
  };

  // Get party join code
  const getPartyCode = async (partyId) => {
    try {
      const { data, error } = await supabase
        .from('parties')
        .select('join_code')
        .eq('id', partyId)
        .single();

      if (error) throw error;

      return data.join_code;
    } catch (error) {
      throw error;
    }
  };

  return (
    <PartyContext.Provider value={{ 
      ...state, 
      createParty, 
      updateParty, 
      deleteParty,
      inviteToParty,
      rsvpToParty,
      addItem,
      removeItem,
      addMessage,
      joinPartyByCode,
      getPartyCode
    }}>
      {children}
    </PartyContext.Provider>
  );
}; 